import { Component, Input, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { formatDate } from '@angular/common';
import { Params     } from '@angular/router';

import { OverviewTableService, SessionStorageService, ToasterService } from '@shared/services';
import { TableHeaderEntryModel, TableBodyEntryModel } from '@shared/models';
import { PhotoDocument, PhotoDocumentSimplified } from '@shared/factories';

@Component({
  selector:     'photo-documents-table',
  templateUrl: '../../table-entries.component.html',
  styleUrls: ['./photo-documents-table.component.sass'],
  host: { class: 'am-flex-column bg-white b-radius-wide overflow' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PhotoDocumentsTableComponent {
  @Input() rows:         PhotoDocument[] | PhotoDocumentSimplified[];
  @Input() paginationId: string;
  @Input() loading:      boolean;

  @Output() headerCallback = new EventEmitter<any>();
  constructor(
    private toasterService:        ToasterService,
    private overviewTableService:  OverviewTableService,
    private sessionStorageService: SessionStorageService
  ) { }

  headerCallbackHandler(res) {
    this.headerCallback.emit(res);
  }

  getTableClass(): string {
    return 'pd-list-wrapper';
  }

  getOptionalValue(field: string): boolean {
    return this[field] || null;
  }

  prepareTableHeader(): TableHeaderEntryModel[] {
    if      (this.paginationId === 'pd-list') return this.preparePhotoDocumetsListTableHeader();
    else if (this.paginationId === 'archive') return this.preparePhotoDocumetsArchiveTableHeader();
  }

  private preparePhotoDocumetsTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Mitarbeiter', class: 'name-cell',     sort_by: ['external_employee_last_name' , 'external_employee_first_name']        },
      { title: 'Dokumenttyp', class: 'subject-cell',  sort_by: ['subject_corrected_or_subject']                                        },

      { title: 'Status',      class: 'status-cell',   sort_by: [this.paginationId === 'pd-list' ? 'read_at' : 'released_to_zvoove_at'] },
      { title: 'Aktion',      class: 'action-cell'                                                                                     }
    ];
  }

  private preparePhotoDocumetsListTableHeader(): TableHeaderEntryModel[] {
    let cols = this.preparePhotoDocumetsTableHeader();
    cols.splice(2, 0, { title: 'Erstellt', class: 'date-cell', sort_by: ['created_at'] });
    return cols;
  }

  private preparePhotoDocumetsArchiveTableHeader(): TableHeaderEntryModel[] {
    let cols = this.preparePhotoDocumetsTableHeader();
    cols.splice(2, 0, { title: 'Eingang',                class: 'date-cell',    sort_by: ['created_at']             });
    cols.splice(5, 0, { title: 'Archiviert',             class: 'archive-cell', sort_by: ['released_to_archive_at'] });
    cols.splice(6, 0, { title: 'An Zvoove Übertragen',   class: 'zvoove-cell',  sort_by: ['released_to_zvoove_at']  });
    return cols;
  }

  prepareTableBody(): TableBodyEntryModel[] {
    if      (this.paginationId === 'pd-list') return this.preparePhotoDocumentsListTableBody();
    else if (this.paginationId === 'archive') return this.preparePhotoDocumentsArchiveTableBody();
  }

  private preparePhotoDocumentsArchiveTableBody(): TableBodyEntryModel[] {
    let rows = this.preparePhotoDocumentsTableBody();
    this.rows.forEach((pd, index) => {
      rows[index].cells.splice(2, 0, { xs_label: 'Eingang',                class: 'date-cell',    value: formatDate(pd.createdAt,                                    'dd.MM.yyyy HH:mm', 'de') });
      rows[index].cells.splice(5, 0, { xs_label: 'Archiviert',             class: 'archive-cell', value: formatDate(pd.releasedToArchiveAt || pd.archivedBySystemAt, 'dd.MM.yyyy',       'de') });
      rows[index].cells.splice(6, 0, { xs_label: 'An Zvoove Übertragen',   class: `zvoove-cell ${pd.releasedToZvooveAt ? '' : 'align-center-zvoove' }`, value: pd.releasedToZvooveAt ? formatDate(pd.releasedToZvooveAt,'dd.MM.yyyy', 'de') : '----' });
    });
    return rows;
  }

  private preparePhotoDocumentsListTableBody(): TableBodyEntryModel[] {
    if (this.loading) return this.preparePreload();
    let rows = this.preparePhotoDocumentsTableBody();
    this.rows.forEach((pd, index) => {
      rows[index].cells.splice(2, 0, { xs_label: 'Erstellt', class: 'date-cell', value: formatDate(pd.createdAt,'dd.MM.yyyy HH:mm', 'de') });
    });
    return rows;
  }

  private preparePreload(): TableBodyEntryModel[] {
    return new Array<TableBodyEntryModel>(10).fill({
      cells: [
        this.overviewTableService.getExternalEmployeeCellPlaceholder(),
        { xs_label: ' ',  class: `subject-cell`,        value: ' '                                           },
        { xs_label: ' ',  class: `date-cell`,           value: ' '                                           },
        { xs_label: null, class: 'status-cell xs-hide', children: [{ class: 'status-icon loading-animate' }] },
        { class: 'action-cell', buttons: [{ class: 'action-cell-button' }]                                   }
      ].filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    });
  }

  private preparePhotoDocumentsTableBody(): TableBodyEntryModel[] {
    return this.rows.map((pd: PhotoDocument): TableBodyEntryModel => ({
      bold: !pd.readAt,
      cells: [
        this.overviewTableService.getExternalEmployeeCell(pd.externalEmployee, { statusDot: this.getStatusPDColor(pd) }),
        { xs_label: 'Dokumenttyp', class: 'subject-cell',  value: pd.activeSubject  },
        { class: 'status-cell xs-hide', children: [
          { class: `status-icon ${this.getStatusPDColor(pd)}`},
          { class: `web-tooltip left short ${this.getStatusPDColor(pd)}`, children: [{
              class: 'tooltip-title', value: this.getStatusText(pd)
            }]
          }]
        },
        { xs_label: '', class: 'action-cell',
          buttons: [{
            class: 'action-cell-button',
            label: pd.releasedToZvooveAt || pd.archivedBySystemAt ? 'Ansehen' : 'Bearbeiten',
            routerLink: ['/time-tracking/pd-details', pd.id],
            routerLinkState: this.getFastNavigationData()
          }]
        }
      ]
    }));
  }

  private getStatusPDColor(pd: PhotoDocument): string {
    if (pd.releasedToZvooveAt)  return 'blue';
    if (pd.releasedToArchiveAt) return 'green';
    if (pd.archivedBySystemAt)  return 'grey';
    if (pd.readAt)              return 'orange';
    if (!pd.readAt)             return 'red';
  }

  private getStatusText(pd: PhotoDocument): string {
    if (pd.releasedToZvooveAt)  return 'An zvoove übertragen';
    if (pd.releasedToArchiveAt) return 'Archiviert';
    if (pd.archivedBySystemAt)  return 'Automatisch archiviert';
    if (pd.readAt)              return 'Gelesen, nicht an zvoove übertragen';
    if (!pd.readAt)             return 'Ungelesen';
  }

  private getFastNavigationData(): Params {
    return ({
      data: this.rows.map(r => ({
        id: r.id,
        page: this.sessionStorageService.pageNumberValue
      }))
    });
  }

}
